// Plays a PNG sequence declared in the template by templates/edl/frame-animation.twig.html.
// Every frame is already an <img> in the container, so the browser preloads and
// decodes them all up front and swapping frames is only a class change. The
// animation sits on its first frame until something calls play().
export class FrameAnimation {

  static DEFAULT_FPS = 8;

  // Every animation found in the page, keyed by its data-name.
  static instances = new Map();

  // Builds one player per [data-frame-animation] container. Called once from EDL.initGame.
  static initAll(root = document) {
    FrameAnimation.instances = new Map();
    root.querySelectorAll("[data-frame-animation]").forEach((el) => {
      const animation = new FrameAnimation(el);
      if (animation.name) FrameAnimation.instances.set(animation.name, animation);
    });
    return FrameAnimation.instances;
  }

  static getByName(name) {
    return FrameAnimation.instances.get(name) || null;
  }

  // Back to the first frame everywhere, for a game reset.
  static stopAll() {
    FrameAnimation.instances.forEach((animation) => animation.stop());
  }

  constructor(root) {
    this.root = root;
    root.frameAnimation = this; // so callers can reach the instance from the element

    this.frames = Array.from(root.querySelectorAll(".frame"));
    this.name = root.dataset.name || "";
    this.fps = parseFloat(root.dataset.fps) || FrameAnimation.DEFAULT_FPS;
    this.loop = root.dataset.loop === "true";

    this.currentFrame = 0;
    this.playing = false;
    this.looping = false;
    this.rafId = null;
    this.lastTime = 0;
    this.accumulator = 0;
    this.resolveCurrent = null;

    this.showFrame(0);
    this.preload();
  }

  // Decode every frame right away so the first run never stutters on a frame
  // the browser has not rasterized yet.
  preload() {
    this.frames.forEach((img) => {
      if (img.decode) img.decode().catch(() => {
      });
    });
  }

  // Runs the sequence from the start. Resolves with true once the last frame is
  // reached, or with false if something interrupted it (a new play, a stop).
  // A looping animation only resolves when it is stopped.
  play(options = {}) {
    if (options.fps) this.setFPS(options.fps);
    this.looping = options.loop !== undefined ? options.loop : this.loop;

    this.settle(false);
    this.showFrame(options.startFrame !== undefined ? options.startFrame : 0);
    return this.startLoop();
  }

  // Stops on the current frame. Reached through the element's own back
  // reference, EDL.setStage holding a sequence where it stands while the stage
  // it belongs to fades away.
  pause() {
    this.stopLoop();
  }

  // Stops and goes back to the first frame.
  stop() {
    this.stopLoop();
    this.settle(false);
    this.showFrame(0);
  }

  // How long a full run takes, in seconds, for a transition that has to last as
  // long as the sequence it plays alongside.
  getDuration() {
    return this.fps ? this.frames.length / this.fps : 0;
  }

  setFPS(fps) {
    this.fps = parseFloat(fps) || FrameAnimation.DEFAULT_FPS;
  }

  showFrame(index) {
    if (!this.frames.length) return;
    const clamped = Math.max(0, Math.min(this.frames.length - 1, index));
    this.frames.forEach((img, i) => img.classList.toggle("current", i === clamped));
    this.currentFrame = clamped;
  }

  startLoop() {
    if (!this.frames.length) return Promise.resolve(true);

    this.playing = true;
    this.lastTime = 0;
    this.accumulator = 0;
    this.currentPromise = new Promise((resolve) => {
      this.resolveCurrent = resolve;
    });
    this.rafId = requestAnimationFrame((now) => this.tick(now));
    return this.currentPromise;
  }

  stopLoop() {
    this.playing = false;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  // Resolves the promise handed out by the last play, so nobody stays awaiting a
  // run that was replaced or stopped.
  settle(completed) {
    const resolve = this.resolveCurrent;
    this.resolveCurrent = null;
    if (resolve) resolve(completed);
  }

  // Time based so a low fps stays exact and never drifts with the frame rate.
  tick(now) {
    if (!this.playing) return;

    if (!this.lastTime) this.lastTime = now;
    this.accumulator += now - this.lastTime;
    this.lastTime = now;

    const frameDuration = 1000 / this.fps;
    while (this.accumulator >= frameDuration) {
      this.accumulator -= frameDuration;
      this.advance();
      if (!this.playing) return;
    }

    this.rafId = requestAnimationFrame((next) => this.tick(next));
  }

  advance() {
    const next = this.currentFrame + 1;

    if (next < this.frames.length) {
      this.showFrame(next);
      return;
    }

    if (this.looping) {
      this.showFrame(0);
      this.dispatch("frame-animation-loop");
      return;
    }

    // Done: hold the last frame and tell whoever is waiting.
    this.stopLoop();
    this.dispatch("frame-animation-complete");
    this.settle(true);
  }

  dispatch(type) {
    this.root.dispatchEvent(new CustomEvent(type, {
      bubbles: true,
      detail: {name: this.name, animation: this}
    }));
  }

}
